<template>
  <div
    class="min-h-screen bg-white dark:bg-[#0c1427]"
  >
    <!-- Header Section -->
    <div 
      class="sticky top-0 z-[5] backdrop-blur-xl bg-white/70 dark:bg-[#0c1427]/70"
  >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <!-- Search Bar -->
          <div class="w-full sm:w-auto">
            <div class="relative group">
          <label
                class="absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 pointer-events-none"
                :class="searchQuery ? 'opacity-0 -translate-x-2' : 'opacity-100'"
          >
                <i class="material-symbols-outlined !text-[22px]" style="color: #9ca3af;">search</i>
          </label>
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="directT('todoList.searchTask')"
                class="w-full sm:w-[380px] h-[48px] pl-12 pr-12 rounded-full border-2 transition-all duration-300 text-sm sm:text-base font-medium shadow-md hover:shadow-lg focus:shadow-xl outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
                :style="{
                  'border-color': searchQuery ? '#8b5cf6' : '#e5e7eb',
                  'background-color': '#ffffff',
                  'color': '#111827',
                  'box-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }"
                :class="{ 'dark:!bg-[#1a2332] dark:!text-white dark:!border-[#2a3441]': true }"
                @focus="handleSearchFocus"
                @blur="handleSearchBlur"
              />
              <div 
                v-if="searchQuery"
                class="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200 hover:scale-110"
                @click="searchQuery = ''"
              >
                <div 
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-colors duration-200"
                  style="background-color: rgba(139, 92, 246, 0.1);"
                  @mouseover="$event.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)'"
                  @mouseleave="$event.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'"
                >
                  <i class="material-symbols-outlined !text-[18px]" style="color: #8b5cf6;">close</i>
      </div>
              </div>
            </div>
          </div>
          
          <!-- Add Task Button -->
        <button
          type="button"
            class="group flex items-center gap-2 px-4 sm:px-6 py-3 rounded-full font-medium text-sm sm:text-base transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg"
            style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); color: #ffffff;"
          @click="openTaskModal(null)"
        >
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:rotate-180"
              style="background-color: rgba(255, 255, 255, 0.2);"
            >
              <i class="material-symbols-outlined !text-[20px]" style="color: #ffffff;">
              add
            </i>
            </div>
            <span class="font-semibold">{{ directT('todoList.addNewTask') }}</span>
        </button>
        </div>
      </div>
    </div>

    <!-- Statistics Section -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <!-- Today's Tasks -->
        <div 
          class="rounded-2xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
          style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-white/80 text-sm font-medium mb-1">{{ directT('todoList.todayTasks') || 'משימות להיום' }}</p>
              <p class="text-white text-3xl font-bold">{{ todayTasksCount }}</p>
      </div>
            <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: rgba(255, 255, 255, 0.2);">
              <i class="material-symbols-outlined !text-[24px] text-white">today</i>
      </div>
      </div>
        </div>

        <!-- This Week's Tasks -->
        <div 
          class="rounded-2xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
          style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-white/80 text-sm font-medium mb-1">{{ directT('todoList.weekTasks') || 'משימות השבוע' }}</p>
              <p class="text-white text-3xl font-bold">{{ weekTasksCount }}</p>
                </div>
            <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: rgba(255, 255, 255, 0.2);">
              <i class="material-symbols-outlined !text-[24px] text-white">date_range</i>
            </div>
          </div>
        </div>

        <!-- Priority Breakdown -->
        <div 
          class="rounded-2xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
          style="background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);"
        >
          <div>
            <p class="text-white text-sm font-medium mb-3">{{ directT('todoList.priorityBreakdown') || 'פילוח לפי דחיפות' }}</p>
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 rounded-full" style="background-color: #ef4444;"></div>
                  <span class="text-white/90 text-xs">{{ directT('todoList.high') }}</span>
                </div>
                <span class="text-white font-semibold text-sm">{{ priorityCount.high }}</span>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 rounded-full" style="background-color: #f59e0b;"></div>
                  <span class="text-white/90 text-xs">{{ directT('todoList.medium') }}</span>
                </div>
                <span class="text-white font-semibold text-sm">{{ priorityCount.medium }}</span>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 rounded-full" style="background-color: #10b981;"></div>
                  <span class="text-white/90 text-xs">{{ directT('todoList.low') }}</span>
                </div>
                <span class="text-white font-semibold text-sm">{{ priorityCount.low }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
      <div class="rounded-2xl p-4 sm:p-6 shadow-lg dark:shadow-xl dark:!bg-[#15203c] dark:!border-[#2a3441]" style="background-color: #ffffff; border: 1px solid #e5e7eb;">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <!-- Filter Title -->
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            <i class="material-symbols-outlined !text-[20px] align-middle mr-2" style="color: #8b5cf6;">filter_list</i>
            {{ directT('todoList.filterTasks') || 'סינון משימות' }}
          </h3>
          
          <!-- Filter Controls -->
          <div class="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <!-- Date Filter -->
            <div class="relative">
              <select
                v-model="dateFilter"
                class="appearance-none px-4 py-2 pr-10 rounded-lg border-2 transition-all duration-200 text-sm font-medium cursor-pointer hover:shadow-md focus:shadow-lg outline-none"
                style="border-color: #e5e7eb; background-color: #f9fafb; color: #374151;"
                @change="$event.target.style.borderColor = dateFilter !== 'all' ? '#8b5cf6' : '#e5e7eb'"
              >
                <option value="all">{{ directT('todoList.allDates') || 'כל התאריכים' }}</option>
                <option value="overdue">{{ directT('todoList.overdue') || 'באיחור' }}</option>
                <option value="today">{{ directT('todoList.today') || 'היום' }}</option>
                <option value="tomorrow">{{ directT('todoList.tomorrow') || 'מחר' }}</option>
                <option value="thisWeek">{{ directT('todoList.thisWeek') || 'השבוע' }}</option>
                <option value="nextWeek">{{ directT('todoList.nextWeek') || 'שבוע הבא' }}</option>
                <option value="thisMonth">{{ directT('todoList.thisMonth') || 'החודש' }}</option>
              </select>
              <i class="material-symbols-outlined !text-[20px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style="color: #6b7280;">expand_more</i>
            </div>

            <!-- Priority Filter -->
            <div class="relative">
              <select
                v-model="priorityFilter"
                class="appearance-none px-4 py-2 pr-10 rounded-lg border-2 transition-all duration-200 text-sm font-medium cursor-pointer hover:shadow-md focus:shadow-lg outline-none"
                style="border-color: #e5e7eb; background-color: #f9fafb; color: #374151;"
                @change="$event.target.style.borderColor = priorityFilter !== 'all' ? '#8b5cf6' : '#e5e7eb'"
              >
                <option value="all">{{ directT('todoList.allPriorities') || 'כל הדחיפויות' }}</option>
                <option value="high">🔴 {{ directT('todoList.high') }}</option>
                <option value="medium">🟡 {{ directT('todoList.medium') }}</option>
                <option value="low">🟢 {{ directT('todoList.low') }}</option>
              </select>
              <i class="material-symbols-outlined !text-[20px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style="color: #6b7280;">expand_more</i>
            </div>

            <!-- Status Filter -->
            <div class="relative">
              <select
                v-model="statusFilter"
                class="appearance-none px-4 py-2 pr-10 rounded-lg border-2 transition-all duration-200 text-sm font-medium cursor-pointer hover:shadow-md focus:shadow-lg outline-none"
                style="border-color: #e5e7eb; background-color: #f9fafb; color: #374151;"
                @change="$event.target.style.borderColor = statusFilter !== 'all' ? '#8b5cf6' : '#e5e7eb'"
              >
                <option value="all">{{ directT('todoList.allStatuses') || 'כל הסטטוסים' }}</option>
                <option value="pending">⏳ {{ directT('todoList.pending') }}</option>
                <option value="in_progress">🔄 {{ directT('todoList.inProgress') }}</option>
                <option value="completed">✅ {{ directT('todoList.completed') }}</option>
                <option value="cancelled">❌ {{ directT('todoList.cancelled') }}</option>
              </select>
              <i class="material-symbols-outlined !text-[20px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style="color: #6b7280;">expand_more</i>
                </div>

            <!-- Clear Filters Button -->
            <button
              v-if="dateFilter !== 'all' || priorityFilter !== 'all' || statusFilter !== 'all'"
              @click="clearFilters"
              class="px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium hover:shadow-md"
              style="background-color: #fee2e2; color: #dc2626;"
              @mouseover="$event.currentTarget.style.backgroundColor = '#fecaca'"
              @mouseleave="$event.currentTarget.style.backgroundColor = '#fee2e2'"
            >
              <i class="material-symbols-outlined !text-[16px] align-middle mr-1">clear</i>
              {{ directT('todoList.clearFilters') || 'נקה סינון' }}
            </button>
          </div>
        </div>

        <!-- Active Filters Display -->
        <div v-if="dateFilter !== 'all' || priorityFilter !== 'all' || statusFilter !== 'all'" class="mt-4 flex flex-wrap items-center gap-2">
          <span class="text-sm font-medium text-gray-600 dark:text-gray-300">{{ directT('todoList.activeFilters') || 'סינון פעיל:' }}</span>
          
          <span v-if="dateFilter !== 'all'" class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium" style="background-color: #ede9fe; color: #7c3aed;">
            <i class="material-symbols-outlined !text-[14px]">calendar_today</i>
            {{ getLocalizedDateFilter(dateFilter) }}
                </span>
          
          <span v-if="priorityFilter !== 'all'" class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium" style="background-color: #fef3c7; color: #d97706;">
            <i class="material-symbols-outlined !text-[14px]">priority_high</i>
            {{ getLocalizedPriority(priorityFilter) }}
                </span>
          
          <span v-if="statusFilter !== 'all'" class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium" style="background-color: #dbeafe; color: #2563eb;">
            <i class="material-symbols-outlined !text-[14px]">flag</i>
            {{ getLocalizedStatus(statusFilter) }}
                </span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="relative">
          <div class="w-16 h-16 rounded-full border-4 animate-spin border-gray-200 border-t-primary-500 dark:border-[#172036] dark:border-t-primary-400">
          </div>
        </div>
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">{{ directT('todoList.loading') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="text-center py-20">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-danger-50 dark:bg-danger-900/20">
          <i class="material-symbols-outlined !text-[32px] text-danger-500">error</i>
        </div>
        <p class="text-sm text-danger-500">{{ errorMessage }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredTasks.length === 0" class="text-center py-20">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-gray-100 dark:bg-[#15203c]">
          <i class="material-symbols-outlined !text-[32px] text-gray-500 dark:text-gray-400">task_alt</i>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ directT('todoList.noTasksFound') }}</p>
      </div>

      <!-- Tasks Grid -->
      <div v-else class="grid gap-4 md:gap-6 md:grid-cols-2">
        <!-- Task Cards -->
        <div
          v-for="task in filteredTasks"
          :key="task.id"
          class="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-1 shadow-lg bg-white dark:bg-[#15203c] border border-gray-100 dark:border-[#172036]"
        >
          <div class="p-6">
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <!-- Left Section -->
              <div class="flex-1 min-w-0">
                <!-- Task Header -->
                <div class="flex items-start gap-3 mb-3">
                  <h3 class="text-base sm:text-lg font-semibold text-black dark:text-white break-words">
                    {{ task.tasktitle }}
                  </h3>
                </div>

                <!-- Task Meta -->
                <div class="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                  <!-- Assigned To -->
                  <div class="flex items-center gap-1 sm:gap-2">
                    <i class="material-symbols-outlined !text-[16px] sm:!text-[18px] text-gray-500 dark:text-gray-400">person</i>
                    <span class="text-gray-500 dark:text-gray-400 truncate max-w-[120px] sm:max-w-none">{{ task.assignedName || directT('todoList.notAssigned') }}</span>
                  </div>

                  <!-- Due Date -->
                  <div class="flex items-center gap-1 sm:gap-2">
                    <i class="material-symbols-outlined !text-[16px] sm:!text-[18px] text-gray-500 dark:text-gray-400">calendar_today</i>
                    <span class="text-gray-500 dark:text-gray-400">{{ formatDate(task.duedate) }}</span>
                  </div>

                  <!-- Priority -->
                  <div class="flex items-center gap-1 sm:gap-2">
                    <div 
                      class="w-2 h-2 rounded-full flex-shrink-0"
                      :class="{
                        'bg-danger-500': task.priority === 'high',
                        'bg-warning-500': task.priority === 'medium',
                        'bg-success-500': task.priority === 'low'
                      }"
                    ></div>
                    <span class="text-gray-500 dark:text-gray-400">{{ getLocalizedPriority(task.priority || 'medium') }}</span>
                  </div>
                </div>

                <!-- Status Badge -->
                <div class="mt-3 sm:mt-4">
                <span
                    class="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium"
                    :class="getStatusClasses(task.status)"
                >
                  {{ getLocalizedStatus(task.status) }}
                </span>
                </div>
              </div>

              <!-- Actions - Always visible on mobile -->
              <div class="flex items-center gap-2 mt-3 sm:mt-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    type="button"
                  class="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 hover:scale-110 text-white shadow-md hover:shadow-lg text-xs sm:text-sm font-medium"
                  style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);"
                    @click="viewTask(task)"
                  >
                  {{ directT('todoList.view') }}
                  </button>
                  <button
                    type="button"
                  class="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 hover:scale-110 text-white shadow-md hover:shadow-lg text-xs sm:text-sm font-medium"
                  style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);"
                    @click="openTaskModal(task)"
                  >
                  {{ directT('todoList.edit') }}
                  </button>
                  <button
                    type="button"
                  class="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 hover:scale-110 text-white shadow-md hover:shadow-lg text-xs sm:text-sm font-medium"
                  style="background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);"
                    @click="deleteTask(task)"
                  >
                  {{ directT('todoList.delete') }}
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Task Modal -->
    <div v-if="showTaskModal" class="fixed inset-0 flex items-center justify-center md:items-center z-50 p-4 mobile-modal-container overflow-hidden">
      <!-- Backdrop with blur -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeTaskModal"></div>
      
      <!-- Modal Content with animation -->
      <div class="relative w-full max-w-lg transform transition-all duration-300 scale-100 opacity-100 mobile-modal-content flex flex-col max-h-[85vh]">
        <div class="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl overflow-hidden flex flex-col h-full">
          <!-- Modal Header with gradient -->
          <div class="relative px-6 py-5 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 flex-shrink-0">
            <div class="flex items-center justify-between" style="display: flex; align-items: center; justify-content: space-between;">
              <div class="flex items-center gap-3" style="display: flex; align-items: center; gap: 12px;">
                <div class="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i class="material-symbols-outlined !text-[24px] text-white">
                    {{ editingTask ? 'edit_note' : 'add_task' }}
                  </i>
                </div>
                <h2 class="text-xl font-bold text-white" style="margin: 0;">
                  {{ editingTask ? directT('todoList.editTask') : directT('todoList.addTask') }}
                </h2>
              </div>
              <button
                @click="closeTaskModal"
                class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 flex items-center justify-center group"
                style="border: none; cursor: pointer;"
              >
                <i class="material-symbols-outlined !text-[20px] text-white group-hover:rotate-90 transition-transform duration-200">
                  close
                </i>
              </button>
            </div>
          </div>
          
          <!-- Modal Body with better spacing -->
          <div class="px-6 py-6 space-y-5 overflow-y-auto custom-scrollbar flex-1">
            <!-- Task Title with icon -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-primary-500">title</i>
                {{ directT('todoList.taskTitle') }}
              </label>
          <input 
            type="text" 
            v-model="taskForm.tasktitle"
                :placeholder="directT('todoList.enterTaskTitle')"
                class="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-sm transition-all duration-200 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 bg-gray-50 dark:bg-[#2c2c2e] text-black dark:text-white placeholder:text-gray-400"
          />
        </div>
        
            <!-- Due Date with icon -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-primary-500">event</i>
                {{ directT('todoList.dueDate') }}
              </label>
          <input 
            type="datetime-local" 
            v-model="taskForm.duedate"
                class="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-sm transition-all duration-200 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 bg-gray-50 dark:bg-[#2c2c2e] text-black dark:text-white"
          />
        </div>
        
            <!-- Assigned To with avatar preview -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-primary-500">person</i>
                {{ directT('todoList.assignedTo') }}
              </label>
          <select 
            v-model="taskForm.assignedto"
                class="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-sm transition-all duration-200 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 bg-gray-50 dark:bg-[#2c2c2e] text-black dark:text-white"
          >
            <option value="">{{ directT('todoList.notAssigned') }}</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.email }}
            </option>
          </select>
        </div>
        
            <!-- Priority and Status Row with visual indicators -->
            <div class="grid grid-cols-2 gap-4">
              <!-- Priority with color coding -->
              <div class="group">
                <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                  <i class="material-symbols-outlined !text-[18px] text-primary-500">priority_high</i>
                  {{ directT('todoList.priority') }}
                </label>
                <div class="relative">
          <select 
            v-model="taskForm.priority"
                    class="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-sm transition-all duration-200 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 bg-gray-50 dark:bg-[#2c2c2e] text-black dark:text-white appearance-none"
          >
                    <option value="high">🔴 {{ directT('todoList.high') }}</option>
                    <option value="medium">🟡 {{ directT('todoList.medium') }}</option>
                    <option value="low">🟢 {{ directT('todoList.low') }}</option>
          </select>
                  <i class="material-symbols-outlined !text-[20px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    expand_more
                  </i>
                </div>
        </div>
        
              <!-- Status with icons -->
              <div class="group">
                <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                  <i class="material-symbols-outlined !text-[18px] text-primary-500">flag</i>
                  {{ directT('todoList.status') }}
                </label>
                <div class="relative">
          <select 
            v-model="taskForm.status"
                    class="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-sm transition-all duration-200 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 bg-gray-50 dark:bg-[#2c2c2e] text-black dark:text-white appearance-none"
          >
                    <option value="pending">⏳ {{ directT('todoList.pending') }}</option>
                    <option value="in_progress">🔄 {{ directT('todoList.inProgress') }}</option>
                    <option value="completed">✅ {{ directT('todoList.completed') }}</option>
                    <option value="cancelled">❌ {{ directT('todoList.cancelled') }}</option>
          </select>
                  <i class="material-symbols-outlined !text-[20px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    expand_more
                  </i>
                </div>
              </div>
        </div>
        
            <!-- Description with character count -->
            <div class="group">
              <label class="flex items-center gap-2 text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                <i class="material-symbols-outlined !text-[18px] text-primary-500">description</i>
                {{ directT('todoList.description') }}
                <span class="ml-auto text-xs font-normal text-gray-500 dark:text-gray-400">
                  {{ taskForm.description?.length || 0 }}/500
                </span>
              </label>
          <textarea 
            v-model="taskForm.description"
                rows="4"
                maxlength="500"
                :placeholder="directT('todoList.enterDescription')"
                class="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-sm transition-all duration-200 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 resize-none bg-gray-50 dark:bg-[#2c2c2e] text-black dark:text-white placeholder:text-gray-400"
          ></textarea>
            </div>
        </div>
        
          <!-- Modal Footer with improved buttons -->
          <div class="px-6 py-4 bg-gray-50 dark:bg-[#15203c] border-t border-gray-200 dark:border-gray-800 flex justify-between gap-3 flex-shrink-0">
          <button 
            @click="closeTaskModal"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 bg-white dark:bg-[#2c2c2e] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3c3c3e] border border-gray-200 dark:border-gray-700"
          >
            {{ directT('todoList.cancel') }}
          </button>
          <button 
            @click="saveTask"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 disabled:opacity-50 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-500/25 disabled:shadow-none"
              :disabled="saveLoading || !taskForm.tasktitle"
          >
              <span v-if="saveLoading" class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ directT('todoList.saving') }}
              </span>
              <span v-else class="flex items-center gap-2">
                <i class="material-symbols-outlined !text-[18px]">
                  {{ editingTask ? 'save' : 'add' }}
                </i>
                {{ editingTask ? directT('todoList.save') : directT('todoList.create') }}
              </span>
          </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- View Task Modal -->
    <div v-if="showViewModal && viewingTask" class="fixed inset-0 flex items-center justify-center md:items-center z-50 p-4 mobile-modal-container overflow-hidden">
      <!-- Backdrop with blur -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeViewModal"></div>
      
      <!-- Modal Content -->
      <div class="relative w-full max-w-lg transform transition-all duration-300 scale-100 opacity-100 mobile-modal-content flex flex-col max-h-[85vh]">
        <div class="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl overflow-hidden flex flex-col h-full">
          <!-- Modal Header with task status color -->
          <div class="relative px-6 py-5 flex-shrink-0"
               :class="{
                 'bg-gradient-to-r from-warning-500 to-warning-600': viewingTask.status === 'pending',
                 'bg-gradient-to-r from-info-500 to-info-600': viewingTask.status === 'in_progress',
                 'bg-gradient-to-r from-success-500 to-success-600': viewingTask.status === 'completed',
                 'bg-gradient-to-r from-danger-500 to-danger-600': viewingTask.status === 'cancelled'
               }">
            <div class="flex items-center justify-between" style="display: flex; align-items: center; justify-content: space-between;">
              <div class="flex items-center gap-3" style="display: flex; align-items: center; gap: 12px;">
                <div class="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i class="material-symbols-outlined !text-[24px] text-white">
                    task
                  </i>
                </div>
                <h2 class="text-xl font-bold text-white" style="margin: 0;">
                  {{ directT('todoList.taskDetails') }}
                </h2>
              </div>
              <button
                @click="closeViewModal"
                class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 flex items-center justify-center group"
                style="border: none; cursor: pointer;"
              >
                <i class="material-symbols-outlined !text-[20px] text-white group-hover:rotate-90 transition-transform duration-200">
                  close
                </i>
              </button>
            </div>
        </div>
        
          <!-- Modal Body with enhanced layout -->
          <div class="px-6 py-6 space-y-5 overflow-y-auto custom-scrollbar flex-1">
            <!-- Task Title with icon -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <i class="material-symbols-outlined !text-[20px] text-gray-400">title</i>
                <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('todoList.title') }}</span>
              </div>
              <h3 class="text-xl font-bold text-black dark:text-white pl-7">{{ viewingTask.tasktitle }}</h3>
        </div>
        
            <!-- Task Details Grid with icons -->
            <div class="grid grid-cols-2 gap-4">
              <!-- Assigned To -->
              <div class="bg-gray-50 dark:bg-[#2c2c2e] p-4 rounded-xl">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-gray-400">person</i>
                  <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ directT('todoList.assignedTo') }}</span>
                </div>
                <span class="text-sm font-semibold text-black dark:text-white">
                  {{ viewingTask.assignedName || directT('todoList.notAssigned') }}
                </span>
        </div>
        
              <!-- Due Date -->
              <div class="bg-gray-50 dark:bg-[#2c2c2e] p-4 rounded-xl">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-gray-400">event</i>
                  <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ directT('todoList.dueDate') }}</span>
                </div>
                <span class="text-sm font-semibold text-black dark:text-white">
                  {{ formatDate(viewingTask.duedate) }}
                </span>
        </div>
        
              <!-- Priority -->
              <div class="bg-gray-50 dark:bg-[#2c2c2e] p-4 rounded-xl">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-gray-400">priority_high</i>
                  <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ directT('todoList.priority') }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <div 
                    class="w-3 h-3 rounded-full"
                    :class="{
                      'bg-danger-500': viewingTask.priority === 'high',
                      'bg-warning-500': viewingTask.priority === 'medium',
                      'bg-success-500': viewingTask.priority === 'low'
                    }"
                  ></div>
                  <span class="text-sm font-semibold text-black dark:text-white">
                    {{ getLocalizedPriority(viewingTask.priority || 'medium') }}
                  </span>
                </div>
        </div>
        
              <!-- Status -->
              <div class="bg-gray-50 dark:bg-[#2c2c2e] p-4 rounded-xl">
                <div class="flex items-center gap-2 mb-2">
                  <i class="material-symbols-outlined !text-[18px] text-gray-400">flag</i>
                  <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ directT('todoList.status') }}</span>
                </div>
                <span 
                  class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold"
                  :class="getStatusClasses(viewingTask.status)"
                >
                  {{ getLocalizedStatus(viewingTask.status) }}
                </span>
              </div>
        </div>
        
            <!-- Description -->
            <div v-if="viewingTask.description" class="space-y-2">
              <div class="flex items-center gap-2">
                <i class="material-symbols-outlined !text-[20px] text-gray-400">description</i>
                <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('todoList.description') }}</span>
              </div>
              <div class="bg-gray-50 dark:bg-[#2c2c2e] p-4 rounded-xl">
                <p class="text-sm leading-relaxed text-black dark:text-white whitespace-pre-wrap">{{ viewingTask.description }}</p>
              </div>
            </div>
        </div>
        
          <!-- Modal Footer -->
          <div class="px-6 py-4 bg-gray-50 dark:bg-[#15203c] border-t border-gray-200 dark:border-gray-800 flex justify-between flex-shrink-0">
            <button 
              @click="editFromViewModal(viewingTask)"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 bg-white dark:bg-[#2c2c2e] text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-200 dark:border-primary-800"
            >
              <span class="flex items-center gap-2">
                <i class="material-symbols-outlined !text-[18px]">edit</i>
                {{ directT('todoList.edit') }}
              </span>
            </button>
          <button 
            @click="closeViewModal"
              class="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 bg-gray-200 dark:bg-[#2c2c2e] text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-[#3c3c3e]"
          >
            {{ directT('todoList.close') }}
          </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Task Modal -->
    <div v-if="showDeleteModal && deletingTask" class="fixed inset-0 flex items-center justify-center md:items-center z-50 p-4 mobile-modal-container">
      <!-- Backdrop with blur -->
      <div class="absolute inset-0" 
           style="background-color: rgba(0, 0, 0, 0.4); backdrop-filter: blur(4px);" 
           @click="closeDeleteModal"></div>
      
      <!-- Modal Content -->
      <div class="relative w-full max-w-md transform transition-all duration-300 scale-100 opacity-100 mobile-modal-content">
        <div style="background-color: #ffffff; border-radius: 24px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden;"
             :style="isDarkMode ? 'background-color: #1c1c1e;' : ''">
          
          <!-- Modal Header -->
          <div style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); padding: 24px;">
            <div class="flex items-center justify-between" style="display: flex; align-items: center; justify-content: space-between;">
              <div class="flex items-center gap-3" style="display: flex; align-items: center; gap: 12px;">
                <div style="width: 40px; height: 40px; background-color: rgba(255, 255, 255, 0.2); backdrop-filter: blur(4px); border-radius: 16px; display: flex; align-items: center; justify-content: center;">
                  <i class="material-symbols-outlined !text-[24px]" style="color: #ffffff;">
                    delete_forever
                  </i>
                </div>
                <h2 style="font-size: 20px; font-weight: 700; color: #ffffff; margin: 0;">
                  {{ directT('todoList.deleteTask') || 'מחיקת משימה' }}
                </h2>
              </div>
              <button
                @click="closeDeleteModal"
                style="width: 32px; height: 32px; background-color: rgba(255, 255, 255, 0.2); border-radius: 50%; transition: all 0.2s; display: flex; align-items: center; justify-content: center; border: none; cursor: pointer;"
                @mouseover="event => event.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'"
                @mouseout="event => event.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'"
              >
                <i class="material-symbols-outlined !text-[20px]" style="color: #ffffff; transition: transform 0.2s;">
                  close
                </i>
              </button>
            </div>
          </div>
          
          <!-- Modal Body -->
          <div style="padding: 32px;">
            <!-- Warning Icon -->
            <div style="width: 64px; height: 64px; background-color: #fee2e2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px;"
                 :style="isDarkMode ? 'background-color: rgba(239, 68, 68, 0.2);' : ''">
              <i class="material-symbols-outlined !text-[32px]" style="color: #ef4444;">
                warning
              </i>
            </div>
            
            <!-- Confirmation Message -->
            <div style="text-align: center; margin-bottom: 24px;">
              <p style="font-size: 18px; font-weight: 600; color: #111827; margin-bottom: 8px;"
                 :style="isDarkMode ? 'color: #ffffff;' : ''">
                {{ (directT('todoList.deleteConfirmation') || 'האם אתה בטוח שברצונך למחוק את המשימה?').replace('{task}', deletingTask.tasktitle) }}
              </p>
              <p style="font-size: 14px; color: #6b7280;"
                 :style="isDarkMode ? 'color: #9ca3af;' : ''">
                {{ directT('todoList.deleteWarning') || 'פעולה זו אינה ניתנת לביטול' }}
              </p>
            </div>
          </div>
          
          <!-- Modal Footer -->
          <div style="padding: 24px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; gap: 12px;"
               :style="isDarkMode ? 'background-color: #15203c; border-color: #374151;' : ''">
            <button 
              @click="closeDeleteModal"
              style="padding: 10px 20px; border-radius: 12px; font-weight: 500; font-size: 14px; transition: all 0.2s; background-color: #e5e7eb; color: #374151;"
              :style="isDarkMode ? 'background-color: #2c2c2e; color: #d1d5db;' : ''"
              @mouseover="event => event.currentTarget.style.backgroundColor = isDarkMode ? '#3c3c3e' : '#d1d5db'"
              @mouseout="event => event.currentTarget.style.backgroundColor = isDarkMode ? '#2c2c2e' : '#e5e7eb'"
            >
              {{ directT('todoList.cancel') || 'ביטול' }}
            </button>
            <button 
              @click="confirmDelete"
              :disabled="deleteLoading"
              style="padding: 10px 20px; border-radius: 12px; font-weight: 500; font-size: 14px; transition: all 0.2s; background-color: #ef4444; color: #ffffff; display: flex; align-items: center; gap: 8px;"
              @mouseover="event => { if (!deleteLoading) event.currentTarget.style.backgroundColor = '#dc2626' }"
              @mouseout="event => { if (!deleteLoading) event.currentTarget.style.backgroundColor = '#ef4444' }"
              :style="deleteLoading ? 'opacity: 0.6; cursor: not-allowed;' : ''"
            >
              <span v-if="deleteLoading" class="animate-spin" style="width: 16px; height: 16px; border: 2px solid #ffffff; border-top-color: transparent; border-radius: 50%;"></span>
              <span v-else class="flex items-center gap-2">
                <i class="material-symbols-outlined !text-[18px]">delete</i>
                {{ directT('todoList.delete') || 'מחיקה' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { supabase } from "@/utils/supabase";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface Task {
  id?: string;
  userid?: string;
  tasktitle: string;
  assignedto?: string;
  assignedName?: string;
  duedate?: string;
  priority?: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  description?: string;
  created_at?: string;
  updated_at?: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "ToDoList",
  setup() {
    const tasks = ref<Task[]>([]);
    const loading = ref(true);
    const errorMessage = ref("");
    const searchQuery = ref("");
    const users = ref<{id: string, email: string}[]>([]);
    
    const showTaskModal = ref(false);
    const showViewModal = ref(false);
    const showDeleteModal = ref(false);
    const editingTask = ref<Task | null>(null);
    const viewingTask = ref<Task | null>(null);
    const deletingTask = ref<Task | null>(null);
    const saveLoading = ref(false);
    const deleteLoading = ref(false);
    
    // Filter states
    const dateFilter = ref('all');
    const priorityFilter = ref('all');
    const statusFilter = ref('all');
    
    // i18n
    const { locale } = useI18n();
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    const taskForm = ref<Task>({
      tasktitle: "",
      assignedto: "",
      duedate: "",
      priority: "medium",
      status: "pending",
      description: "",
    });
    
    const fetchTasks = async () => {
      try {
        loading.value = true;
        errorMessage.value = "";
        
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          throw new Error(userError.message);
        }
        
        if (!userData || !userData.user) {
          throw new Error("No authenticated user found");
        }
        
        const { data, error } = await supabase
          .from('todolist')
          .select('*')
          .or(`userid.eq.${userData.user.id},assignedto.eq.${userData.user.id}`)
          .order('created_at', { ascending: false });
          
        if (error) {
          throw new Error(error.message);
        }
        
        // Get user names for assignedto
        const userIds = data?.filter(task => task.assignedto).map(task => task.assignedto) || [];
        
        if (userIds.length > 0) {
          const { data: usersData, error: usersError } = await supabase
            .from('user_profiles')
            .select('user_id, email, display_name')
            .in('user_id', userIds);
            
          if (!usersError && usersData) {
            const userMap = new Map();
            usersData.forEach(user => {
              // Use display_name if available, otherwise fall back to email
              userMap.set(user.user_id, user.display_name || user.email);
            });
            
            data?.forEach(task => {
              if (task.assignedto && userMap.has(task.assignedto)) {
                task.assignedName = userMap.get(task.assignedto);
              }
            });
          }
        }
        
        tasks.value = (data as any[]) || [];
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
        errorMessage.value = error instanceof Error ? error.message : "Failed to load tasks";
      } finally {
        loading.value = false;
      }
    };
    
    const filteredTasks = computed(() => {
      let result = tasks.value;
      
      // Apply search filter
      if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase();
        result = result.filter(task => 
        task.tasktitle.toLowerCase().includes(query) ||
        (task.description && task.description.toLowerCase().includes(query)) ||
        (task.assignedName && task.assignedName.toLowerCase().includes(query))
      );
      }
      
      // Apply date filter
      if (dateFilter.value !== 'all') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        result = result.filter(task => {
          if (!task.duedate) return false;
          const taskDate = new Date(task.duedate);
          taskDate.setHours(0, 0, 0, 0);
          
          switch (dateFilter.value) {
            case 'overdue':
              return taskDate < today && task.status !== 'completed' && task.status !== 'cancelled';
            case 'today':
              return taskDate.getTime() === today.getTime();
            case 'tomorrow': {
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);
              return taskDate.getTime() === tomorrow.getTime();
            }
            case 'thisWeek': {
              const weekEnd = new Date(today);
              weekEnd.setDate(weekEnd.getDate() + (6 - today.getDay()));
              return taskDate >= today && taskDate <= weekEnd;
            }
            case 'nextWeek': {
              const nextWeekStart = new Date(today);
              nextWeekStart.setDate(nextWeekStart.getDate() + (7 - today.getDay()));
              const nextWeekEnd = new Date(nextWeekStart);
              nextWeekEnd.setDate(nextWeekEnd.getDate() + 6);
              return taskDate >= nextWeekStart && taskDate <= nextWeekEnd;
            }
            case 'thisMonth':
              return taskDate.getMonth() === today.getMonth() && taskDate.getFullYear() === today.getFullYear();
            default:
              return true;
          }
        });
      }
      
      // Apply priority filter
      if (priorityFilter.value !== 'all') {
        result = result.filter(task => task.priority === priorityFilter.value);
      }
      
      // Apply status filter
      if (statusFilter.value !== 'all') {
        result = result.filter(task => task.status === statusFilter.value);
      }
      
      return result;
    });
    
    const fetchUsers = async () => {
      try {
        // First, get the current user's org_id
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError || !userData || !userData.user) {
          console.warn('Failed to get current user:', userError);
          return;
        }

        // Get current user's profile to find their org_id
        const { data: currentUserProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', userData.user.id)
          .single();

        if (profileError || !currentUserProfile) {
          console.warn('Failed to get current user profile:', profileError);
          return;
        }

        // Fetch all users with the same org_id
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, user_id, email, display_name, role, org_id')
          .eq('org_id', currentUserProfile.org_id);
          
        if (error) {
          console.warn('Failed to fetch users from database:', error);
          return;
        }
        
        users.value = (data as any[])?.map((user: any) => ({
          id: user.user_id as string,
          email: (user.display_name || user.email) as string
        })) || [];
      } catch (error) {
        // Silently handle user fetch error to avoid breaking UI
        console.warn('Failed to fetch users:', error);
      }
    };
    
    const openTaskModal = (task: Task | null) => {
      fetchUsers(); // Load users for dropdown
      
      if (task) {
        editingTask.value = task;
        taskForm.value = {
          id: task.id,
          tasktitle: task.tasktitle,
          duedate: task.duedate ? formatDateForInput(task.duedate) : "",
          assignedto: task.assignedto || "",
          priority: task.priority || "medium",
          status: task.status,
          description: task.description || "",
          assignedName: task.assignedName
        };
      } else {
        editingTask.value = null;
        taskForm.value = {
          id: "",
          tasktitle: "",
          duedate: "",
          assignedto: "",
          priority: "medium",
          status: "pending",
          description: "",
        };
      }
      showTaskModal.value = true;
    };
    
    const closeTaskModal = () => {
      showTaskModal.value = false;
    };
    
    const viewTask = (task: Task) => {
      viewingTask.value = task;
      showViewModal.value = true;
    };
    
    const closeViewModal = () => {
      showViewModal.value = false;
    };

    const editFromViewModal = (task: Task) => {
      // Close the view modal first
      closeViewModal();
      // Then open the edit modal
      openTaskModal(task);
    };
    
    const saveTask = async () => {
      try {
        saveLoading.value = true;
        
        if (!taskForm.value.tasktitle) {
          throw new Error("Task title is required");
        }
        
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError || !userData || !userData.user) {
          throw new Error("Authentication required");
        }
        
        const taskData = {
          tasktitle: taskForm.value.tasktitle,
          duedate: taskForm.value.duedate ? new Date(taskForm.value.duedate).toISOString() : null,
          assignedto: taskForm.value.assignedto || null,
          priority: taskForm.value.priority,
          status: taskForm.value.status,
          description: taskForm.value.description || null
        };
        
        let newTaskId;
        
        if (editingTask.value) {
          // Update existing task
          const { error } = await supabase
            .from('todolist')
            .update({
              ...taskData,
              updated_at: new Date().toISOString()
            })
            .eq('id', editingTask.value.id);
            
          if (error) throw new Error(error.message);
        } else {
          // Create new task
          const { data, error } = await supabase
            .from('todolist')
            .insert({
              ...taskData,
              userid: userData.user.id
            })
            .select();
            
          if (error) throw new Error(error.message);
          
          // Store the new task ID
          if (data && data.length > 0) {
            newTaskId = data[0].id;
            
            // If task is assigned to someone, create a notification for them
            if (taskData.assignedto) {
              
              try {
                // Create a notification for the assigned user
                const notificationResult = await createNotification(
                  taskData.assignedto,
                  `New task: ${taskData.tasktitle}`
                );
                
                
              } catch (notificationError) {
                console.warn('Failed to create notification:', notificationError);
                // לא זורקים שגיאה כדי לא לעצור את התהליך
              }
            }
          }
        }
        
        await fetchTasks();
        closeTaskModal();
      } catch (error) {
        console.error('Failed to save task:', error);
        errorMessage.value = error instanceof Error ? error.message : "Failed to save task";
      } finally {
        saveLoading.value = false;
      }
    };
    
    // Function to create a notification
    const createNotification = async (userId: string, content: string) => {
      try {
        
        
        const notificationData = {
          userid: userId,
          content: content,
          type: 'alert',
          status: 'unread'
          // זמן יתווסף אוטומטית בדאטאבייס דרך ה-created_at
        };
        
        const { data, error } = await supabase
          .from('notifications')
          .insert(notificationData)
          .select();
          
        if (error) {
          console.error('Failed to insert notification:', error);
          return null;
        }
        
        
        return data;
      } catch (error) {
        console.error('Failed to create notification:', error);
        return null;
      }
    };
    
    const deleteTask = async (task: Task) => {
      deletingTask.value = task;
      showDeleteModal.value = true;
    };
    
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deletingTask.value = null;
    };
    
    const confirmDelete = async () => {
      if (!deletingTask.value) return;
      
      try {
        deleteLoading.value = true;
        
        const { error } = await supabase
          .from('todolist')
          .delete()
          .eq('id', deletingTask.value.id);
          
        if (error) throw new Error(error.message);
        
        await fetchTasks();
        closeDeleteModal();
      } catch (error) {
        console.error('Failed to delete task:', error);
        errorMessage.value = error instanceof Error ? error.message : "Failed to delete task";
      } finally {
        deleteLoading.value = false;
      }
    };
    
    const formatDate = (dateString?: string) => {
      if (!dateString) return 'Not set';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // פונקציה להמרת תאריך לפורמט datetime-local (זמן מקומי)
    const formatDateForInput = (dateString: string) => {
      const date = new Date(dateString);
      // נקבל את הזמן המקומי במקום UTC
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    const capitalizeFirstLetter = (string: string) => {
      return string.charAt(0).toUpperCase() + string.slice(1);
    };
    
    const getStatusClasses = (status: string) => {
      switch (status) {
        case 'pending':
          return 'bg-warning-50 dark:bg-warning-900/20 text-warning-600 dark:text-warning-400';
        case 'in_progress':
          return 'bg-success-50 dark:bg-success-900/20 text-success-600 dark:text-success-400';
        case 'completed':
          return 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400';
        case 'cancelled':
          return 'bg-danger-50 dark:bg-danger-900/20 text-danger-600 dark:text-danger-400';
        default:
          return 'bg-warning-50 dark:bg-warning-900/20 text-warning-600 dark:text-warning-400';
      }
    };
    
    const getLocalizedPriority = (priority: string) => {
      switch (priority) {
        case 'high':
          return directT('todoList.high');
        case 'medium':
          return directT('todoList.medium');
        case 'low':
          return directT('todoList.low');
        default:
          return directT('todoList.medium');
      }
    };
    
    const getLocalizedStatus = (status: string) => {
      switch (status) {
        case 'pending':
          return directT('todoList.pending');
        case 'in_progress':
          return directT('todoList.inProgress');
        case 'completed':
          return directT('todoList.completed');
        case 'cancelled':
          return directT('todoList.cancelled');
        default:
          return directT('todoList.pending');
      }
    };
    
    const getLocalizedDateFilter = (filter: string) => {
      switch (filter) {
        case 'overdue':
          return directT('todoList.overdue') || 'באיחור';
        case 'today':
          return directT('todoList.today') || 'היום';
        case 'tomorrow':
          return directT('todoList.tomorrow') || 'מחר';
        case 'thisWeek':
          return directT('todoList.thisWeek') || 'השבוע';
        case 'nextWeek':
          return directT('todoList.nextWeek') || 'שבוע הבא';
        case 'thisMonth':
          return directT('todoList.thisMonth') || 'החודש';
        default:
          return filter;
      }
    };
    
    const clearFilters = () => {
      dateFilter.value = 'all';
      priorityFilter.value = 'all';
      statusFilter.value = 'all';
    };
    
    const handleSearchFocus = (event: FocusEvent) => {
      const target = event.target as HTMLInputElement;
      if (target) {
        target.style.borderColor = '#8b5cf6';
        target.style.backgroundColor = '#ffffff';
        target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
        
        // Check if dark mode is active
        if (document.documentElement && document.documentElement.classList.contains('dark')) {
          target.style.backgroundColor = '#1a2332';
          target.style.color = '#ffffff';
        }
      }
    };
    
    const handleSearchBlur = (event: FocusEvent) => {
      const target = event.target as HTMLInputElement;
      if (target) {
        target.style.borderColor = searchQuery.value ? '#8b5cf6' : '#e5e7eb';
        target.style.backgroundColor = '#ffffff';
        target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        
        // Check if dark mode is active
        if (document.documentElement && document.documentElement.classList.contains('dark')) {
          target.style.backgroundColor = '#1a2332';
          target.style.borderColor = searchQuery.value ? '#8b5cf6' : '#2a3441';
          target.style.color = '#ffffff';
        }
      }
    };
    
    onMounted(() => {
      fetchTasks();
    });

    // Statistics computed properties
    const todayTasksCount = computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      return tasks.value.filter(task => {
        if (!task.duedate) return false;
        const taskDate = new Date(task.duedate);
        return taskDate >= today && taskDate < tomorrow && task.status !== 'completed' && task.status !== 'cancelled';
      }).length;
    });

    const weekTasksCount = computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekFromNow = new Date(today);
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      
      return tasks.value.filter(task => {
        if (!task.duedate) return false;
        const taskDate = new Date(task.duedate);
        return taskDate >= today && taskDate <= weekFromNow && task.status !== 'completed' && task.status !== 'cancelled';
      }).length;
    });

    const priorityCount = computed(() => {
      const counts = {
        high: 0,
        medium: 0,
        low: 0
      };
      
      tasks.value.forEach(task => {
        if (task.status !== 'completed' && task.status !== 'cancelled') {
          const priority = task.priority || 'medium';
          counts[priority]++;
        }
      });
      
      return counts;
    });

    // Dark mode detection
    const isDarkMode = computed(() => {
      return document.documentElement && document.documentElement.classList.contains('dark');
    });



    return {
      tasks,
      loading,
      errorMessage,
      searchQuery,
      filteredTasks,
      showTaskModal,
      showViewModal,
      showDeleteModal,
      editingTask,
      viewingTask,
      deletingTask,
      taskForm,
      saveLoading,
      deleteLoading,
      users,
      openTaskModal,
      closeTaskModal,
      viewTask,
      closeViewModal,
      editFromViewModal,
      saveTask,
      deleteTask,
      closeDeleteModal,
      confirmDelete,
      formatDate,
      formatDateForInput,
      capitalizeFirstLetter,
      getStatusClasses,
      getLocalizedPriority,
      getLocalizedStatus,
      directT,
      todayTasksCount,
      weekTasksCount,
      priorityCount,
      dateFilter,
      priorityFilter,
      statusFilter,
      getLocalizedDateFilter,
      clearFilters,
      handleSearchFocus,
      handleSearchBlur,
      isDarkMode
    };
  },
});
</script>

<style scoped>
/* Custom scrollbar for better UX */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-700 rounded;
}

::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400 dark:bg-gray-600;
}

/* Smooth transitions for interactive elements */
input:focus,
select:focus,
textarea:focus {
  outline: none;
}

/* Animation for loading spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Mobile Modal Positioning */
@media (max-width: 768px) {
  .mobile-modal-container {
    align-items: flex-start !important;
    padding-top: env(safe-area-inset-top, 80px) !important;
  }
  
  .mobile-modal-content {
    margin-top: 20px;
    max-height: calc(100vh - env(safe-area-inset-top, 80px) - 100px);
    overflow-y: auto;
  }
}

/* iOS specific adjustments */
@supports (padding: max(0px)) {
  @media (max-width: 768px) {
    .mobile-modal-container {
      padding-top: max(env(safe-area-inset-top), 80px) !important;
    }
    
    .mobile-modal-content {
      max-height: calc(100vh - max(env(safe-area-inset-top), 80px) - 100px);
    }
  }
}
</style>
